import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RowActionButton, RowActionGroup } from '../../../components/ui/RowActions';
import type { InstanceRow } from '../instanceTypes';
import { FEATURE_FLAGS } from '../../../shared/featureFlags';

const primaryItemSx = (theme: Theme) =>
  ({
    color: 'text.label',
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
    },
  }) as const;

const deleteItemSx = (theme: Theme) =>
  ({
    color: 'error.main',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
  }) as const;

interface InstanceRowActionsProps {
  row: InstanceRow;
  isGenerating: boolean;
  /** `modify` on the `instances` line — Generate key and Edit (ADR-040). */
  canModify: boolean;
  /** `delete` on the `instances` line (ADR-040). */
  canDelete: boolean;
  onEdit: (row: InstanceRow) => void;
  onGenerateKey: (row: InstanceRow) => void;
  onDelete: (row: InstanceRow) => void;
}

export function InstanceRowActions({
  row,
  isGenerating,
  canModify,
  canDelete,
  onEdit,
  onGenerateKey,
  onDelete,
}: InstanceRowActionsProps) {
  const { t } = useTranslation('instances');
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = `instance-actions-${row.id}`;

  const handleOpen = (e: MouseEvent) => {
    setAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClose = () => setAnchorEl(null);

  // No entry the caller may use → no menu at all (ADR-040 D4).
  if (!canModify && !canDelete) return null;

  const generateKeyLabel =
    row.registeredAt !== null ? t('actions.generateKeyRenew') : t('actions.generateKey');

  return (
    <RowActionGroup>
      <RowActionButton
        title={t('columns.actions')}
        ariaLabel={`${t('columns.actions')} — ${row.name || row.id}`}
        aria-controls={menuId}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleOpen}
      >
        <MoreVertIcon />
      </RowActionButton>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 2, sx: { minWidth: theme.spacing(25) } } }}
      >
        {canModify && FEATURE_FLAGS.EMAIL_ON_KEY_GENERATION && (
          <MenuItem
            disabled={isGenerating}
            aria-busy={isGenerating}
            onClick={() => {
              handleClose();
              onGenerateKey(row);
            }}
            sx={primaryItemSx}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {isGenerating ? (
                <CircularProgress aria-hidden="true" size={theme.iconSize.sm} />
              ) : (
                <VpnKeyIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{generateKeyLabel}</ListItemText>
          </MenuItem>
        )}

        {canModify && (
          <MenuItem
            onClick={() => {
              handleClose();
              onEdit(row);
            }}
            sx={primaryItemSx}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem
            onClick={() => {
              handleClose();
              onDelete(row);
            }}
            sx={deleteItemSx}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('actions.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </RowActionGroup>
  );
}
